import React from 'react';
import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import { segment } from '@actions/analytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import { mediaQuery } from '@styles/index';
import { UAITS_EVENT_MAP } from './constants';
import { useTestSeriesData } from './useTestSeriesData';

const HeroCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 150px 0;
  align-items: center;
  justify-content: center;

  ${mediaQuery.uptoMobile} {
    padding: 30px 10px;
  }
`;

const TagContainer = styled.div`
  width: fit-content;
  display: flex;
  padding: 8px 20px 8px 18px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 27px;
  background: var(--color-blue-bg);

  ${mediaQuery.uptoMobile} {
    padding: 4px 12px;
  }
`;

const Tagline = styled.p`
  color: var(--color-blue-text);
  text-align: center;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -1px;

  ${mediaQuery.uptoMobile} {
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
    letter-spacing: -1px;
    text-align: center;
  }
`;

const MainHeadline = styled.p`
  color: var(--color-primary-focus);
  max-width: 1000px;
  text-align: center;
  font-size: 80px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -1px;

  ${mediaQuery.uptoMobile} {
    font-weight: 700;
    font-size: 40px;
    line-height: 130%;
    letter-spacing: -2px;
    text-align: center;
  }
`;

const CTAButton = styled.a`
  margin-top: 50px;
  background-color: var(--color-primary-focus);
  padding: 12px 80px;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 18px;
  line-height: 23px;
  letter-spacing: 0px;

  color: var(--color-base-1);

  ${mediaQuery.uptoMobile} {
    margin-top: 20px;
  }
`;

const SupportingTextContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const SupportingImage = styled.img`
  height: 24px;
`;

const SupportingText = styled.p`
  color: var(--color-text-secondary);
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
`;

const HeroCard = () => {
  const { data } = useTestSeriesData();
  const meData = useMyInfo(getMe);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  if (!data?.heroSection) return null;

  const {
    tagline = '',
    mainHeadline = '',
    ctaButtonLabel = '',
    supportingText = ''
  } = data.heroSection || {};

  const handleCTAClick = () => {
    segment.track(UAITS_EVENT_MAP.CLICKED_ON_EXPLORE, {
      userId: meData?.uid,
      current_page_url:
        typeof window !== 'undefined' ? window.location.href : '',
      login_status: isLoggedIn,
      page_title: 'UAITS'
    });
  };

  return (
    <HeroCardWrapper>
      <TagContainer>
        <Tagline>{tagline}</Tagline>
      </TagContainer>
      <MainHeadline as="h1">{mainHeadline}</MainHeadline>
      <CTAButton onClick={handleCTAClick} href="#test-series">
        {ctaButtonLabel}
      </CTAButton>
      <SupportingTextContainer>
        <SupportingImage src={getImage('testseries/support-gif.gif')} />
        <SupportingText>{supportingText}</SupportingText>
      </SupportingTextContainer>
    </HeroCardWrapper>
  );
};

export default HeroCard;
